import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Check,
  Clock,
  ListChecks,
  ListVideo,
  Play,
  X,
} from 'lucide-react';
import { useSidebar } from '../../context/SidebarContext';
import type { CursoAcademy, LeccionCurso } from './academyApi';
import { formatearDuracion } from './academyFormato';
import { BotonCertificado } from './certificado/BotonCertificado';
import { EXAMEN_HABILITADO } from './examenHabilitado';
import type { ProgresoCurso } from './progresoDerivado';
import { useEsperaExamen } from './useEsperaExamen';

/**
 * El recorrido del curso **en teléfono** — ADR-2026-08-25-en-mobile-el-curso-se-recorre-sin-el-panel.
 *
 * En desktop el panel de la derecha muestra a la vez el progreso, las catorce
 * lecciones y el acceso al examen, y no molesta porque vive en una columna propia.
 * Apilado en un teléfono ese mismo panel es una lista larguísima entre el video y el
 * resumen: hay que scrollearla entera para llegar al texto de la lección, y la acción
 * que de verdad se quiere —seguir— queda a mitad de camino.
 *
 * Debajo de `lg` el panel se esconde y lo reemplazan tres piezas:
 *
 * - `BarraLeccionesMobile` — arriba del video: en qué lección vas, cuánto llevás, y
 *   el acceso a la lista.
 * - `SheetLecciones` — la lista completa, en una hoja que sube desde abajo. Está
 *   guardada hasta que se la pide, que es como se usa: se abre para saltar a una
 *   lección puntual y se cierra.
 * - `BarraSiguiente` — fija abajo, siempre visible: **una** acción, la que
 *   corresponde según dónde estás.
 */

/* ── La tira de arriba del video ─────────────────────────────────────────────── */

export function BarraLeccionesMobile({
  titulo,
  indice,
  total,
  progreso,
  onAbrirLista,
}: {
  /** El curso, numerado — el mismo texto que en desktop es el `h1` de la pantalla. */
  titulo: string;
  /** Índice 0-based de la lección actual. */
  indice: number;
  total: number;
  progreso: ProgresoCurso;
  onAbrirLista: () => void;
}) {
  return (
    /* Tarjeta blanca sobre el gris del shell, con el mismo borde que el panel de
       desktop: despegada del fondo, el bloque se lee como UNA cosa —dónde estás en
       este curso— en vez de tres datos sueltos flotando arriba del video. */
    <div className="mb-3 rounded-2xl border border-black/[0.06] bg-white p-3.5 shadow-[0_1px_2px_rgb(var(--brand-black-rgb)_/_0.04)] lg:hidden">
      {/* El curso va acá y chiquito, y el `h1` grande se esconde en mobile
          (ADR-2026-08-25-en-mobile-el-titulo-del-curso-vive-en-la-tarjeta-de-lecciones):
          ocupaba dos renglones enormes arriba de todo para decir algo que ya se sabe
          —se acaba de entrar al curso— y empujaba el video fuera del pliegue. */}
      <p className="truncate text-[12.5px] font-medium text-black/45">{titulo}</p>

      <div className="mt-1.5 flex items-center justify-between gap-3">
        <p className="text-[12.5px] font-semibold uppercase tracking-[0.06em] text-brand-primary">
          Lección {indice + 1} de {total}
        </p>
        {/* El acceso a la lista es un BOTÓN con su etiqueta y no sólo un ícono: en
            esta tira no hay nada más que compita por el toque, y "Lecciones" escrito
            es lo que dice que hay una lista atrás. */}
        <button
          type="button"
          onClick={onAbrirLista}
          className="-mr-1.5 inline-flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-[13.5px] font-semibold text-brand-black/85 transition-colors active:bg-black/[0.05]"
        >
          <ListVideo className="h-4 w-4" strokeWidth={2.1} />
          Lecciones
        </button>
      </div>

      <div className="mt-2 flex items-center gap-2.5">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/[0.07]">
          <div
            className={`h-full rounded-full transition-[width] duration-500 ${
              progreso.completo ? 'bg-emerald-500' : 'bg-brand-primary'
            }`}
            style={{ width: `${progreso.porcentaje}%` }}
          />
        </div>
        <span
          className={`shrink-0 text-[12px] font-bold tabular-nums ${
            progreso.completo ? 'text-emerald-600' : 'text-brand-primary'
          }`}
        >
          {progreso.porcentaje}%
        </span>
      </div>
    </div>
  );
}

/* ── Un renglón de la lista ──────────────────────────────────────────────────── */

/**
 * Una lección en una lista. Lo comparten el panel de desktop y la hoja de mobile:
 * son la misma lista en dos lugares, y duplicar el renglón garantizaba que un día
 * se vieran distintos.
 */
export function ItemLeccion({
  leccion,
  numero,
  esActual,
  vista,
  onIr,
}: {
  leccion: LeccionCurso;
  numero: number;
  esActual: boolean;
  vista: boolean;
  onIr: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onIr}
      className={`flex w-full items-start gap-3 rounded-xl p-2.5 text-left transition-colors ${
        esActual ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.085)]' : 'hover:bg-black/[0.03] active:bg-black/[0.05]'
      }`}
    >
      {/* Un solo indicador con tres estados: vista (tilde), actual (play) y
          pendiente (número). */}
      <span
        className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-lg text-[11.5px] font-bold ${
          vista
            ? 'bg-emerald-50 text-emerald-600'
            : esActual
              ? 'bg-brand-primary text-white'
              : 'bg-black/[0.05] text-black/40'
        }`}
      >
        {vista ? (
          <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
        ) : esActual ? (
          <Play className="h-3 w-3 translate-x-[1px] fill-current" />
        ) : (
          numero
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span
          className={`block text-[13.5px] leading-snug ${
            esActual ? 'font-semibold text-brand-primary' : 'font-medium text-brand-black/85'
          }`}
        >
          {leccion.titulo}
        </span>
        <span className="mt-0.5 block text-[12px] tabular-nums text-black/40">
          {formatearDuracion(leccion.duracionSeg)}
        </span>
      </span>
    </button>
  );
}

/* ── La hoja con la lista completa ───────────────────────────────────────────── */

interface PropsSheet {
  curso: CursoAcademy;
  progreso: ProgresoCurso;
  leccionActualId: string;
  onCerrar: () => void;
}

/**
 * La hoja, sin el portal ni el fondo.
 *
 * Va aparte para poder dibujarla fuera del navegador —`createPortal` necesita un
 * `document` y en un render de servidor no hay— y así verificar el layout con una
 * captura sin montar la app entera.
 */
export function PanelLecciones({
  curso,
  progreso,
  leccionActualId,
  onCerrar,
}: PropsSheet) {
  const navigate = useNavigate();
  const hayRevision = EXAMEN_HABILITADO && progreso.ultimoIntento !== null;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="max-h-[85vh] w-full overflow-hidden rounded-t-[22px] bg-white shadow-[0_-2px_24px_rgb(var(--brand-black-rgb)_/_0.18)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {/* Agarradera: la señal de que la hoja se cierra tirando hacia abajo —y de
          que es una hoja y no una pantalla nueva—. */}
      <div className="flex justify-center pb-1 pt-2.5">
        <span className="h-1 w-9 rounded-full bg-black/15" />
      </div>

      <div className="flex items-start justify-between gap-3 px-4 pb-3">
        <div className="min-w-0">
          <h2 className="text-[16px] font-bold tracking-[-0.01em] text-brand-black/90">
            Plan de lecciones
          </h2>
          <p className="mt-0.5 text-[12.5px] text-black/45">
            {progreso.vistas} de {progreso.total} completadas ·{' '}
            <span
              className={`font-semibold tabular-nums ${
                progreso.completo ? 'text-emerald-600' : 'text-brand-primary'
              }`}
            >
              {progreso.porcentaje}%
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar"
          className="-mr-1 shrink-0 rounded-full bg-black/[0.05] p-2 text-black/50 transition-colors active:bg-black/[0.1]"
        >
          <X className="h-4 w-4" strokeWidth={2.2} />
        </button>
      </div>

      <div className="px-4">
        <div className="h-1.5 overflow-hidden rounded-full bg-black/[0.07]">
          <div
            className={`h-full rounded-full transition-[width] duration-500 ${
              progreso.completo ? 'bg-emerald-500' : 'bg-brand-primary'
            }`}
            style={{ width: `${progreso.porcentaje}%` }}
          />
        </div>
      </div>

      {/* La lista es lo ÚNICO que scrollea: el encabezado con el progreso se
          queda, así se ve dónde vas mientras se busca la lección. */}
      <ul className="mt-2 max-h-[52vh] overflow-y-auto overscroll-contain px-2 pb-2">
        {curso.lecciones.map((l, i) => (
          <li key={l.id}>
            <ItemLeccion
              leccion={l}
              numero={i + 1}
              esActual={l.id === leccionActualId}
              vista={progreso.vistasIds.includes(l.id)}
              onIr={() => {
                navigate(`/academia/${curso.slug}/${l.slug}`);
                onCerrar();
              }}
            />
          </li>
        ))}
      </ul>

      {/* La revisión del examen vive acá porque en mobile el panel —donde estaba—
          ya no se muestra, y es la única forma de llegar a ella. */}
      {hayRevision && (
        <div className="border-t border-black/[0.06] p-3">
          <button
            type="button"
            onClick={() => navigate(`/academia/${curso.slug}/revision`)}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-black/[0.04] py-2.5 text-[13.5px] font-semibold text-black/60 transition-colors active:bg-black/[0.08]"
          >
            <ListChecks className="h-3.5 w-3.5" strokeWidth={2.2} />
            Ver mis respuestas
          </button>
        </div>
      )}
    </div>
  );
}

export function SheetLecciones(props: PropsSheet) {
  const { onCerrar } = props;

  /* Igual que en el modal del certificado: el bloqueo de scroll se monta UNA vez y el
     Escape lee el callback por ref. Con el efecto dependiendo de `onCerrar` —que el
     padre recrea en cada render— la segunda vuelta capturaba `'hidden'` como valor
     previo y al cerrar la hoja el `body` se quedaba sin scroll. */
  const cerrarRef = useRef(onCerrar);
  cerrarRef.current = onCerrar;
  const hojaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previo;
    };
  }, []);

  /* El foco vive adentro de la hoja mientras está abierta
     (ADR-2026-10-07-la-hoja-de-lecciones-se-lleva-el-foco): `aria-modal` sólo lo
     ANUNCIA, no lo hace. Al abrir entra al primer control (el de cerrar), Tab y
     Shift+Tab dan la vuelta adentro en vez de caer en la lección tapada, y al cerrar
     vuelve a lo que lo tenía antes —el botón "Lecciones"—. */
  useEffect(() => {
    const previo = document.activeElement as HTMLElement | null;
    const enfocables = () =>
      Array.from(
        hojaRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
    enfocables()[0]?.focus();

    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cerrarRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const lista = enfocables();
      if (lista.length === 0) return;
      const primero = lista[0];
      const ultimo = lista[lista.length - 1];
      const adentro = hojaRef.current?.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === primero || !adentro)) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && (document.activeElement === ultimo || !adentro)) {
        e.preventDefault();
        primero.focus();
      }
    };
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('keydown', alTeclear);
      previo?.focus?.();
    };
  }, []);

  return createPortal(
    <div
      ref={hojaRef}
      className="fixed inset-0 z-[9998] flex items-end bg-brand-black/50 backdrop-blur-[2px] lg:hidden"
      onClick={onCerrar}
      role="dialog"
      aria-modal="true"
      aria-label="Lecciones del curso"
    >
      <PanelLecciones {...props} />
    </div>,
    document.body,
  );
}

/* ── La barra fija de abajo ──────────────────────────────────────────────────── */

/**
 * **Una** acción, siempre visible: la que sigue según dónde estás.
 *
 * El orden importa y no es el del recorrido sino el de la intención: con el curso
 * terminado, lo que se quiere es el examen (o el certificado) aunque estés parado en
 * la lección 3 repasando. Mientras queden lecciones, seguir.
 *
 *   aprobado          → Ver certificado
 *   todas vistas      → Tomar examen (o cuánto falta, si hay espera corriendo)
 *   hay una siguiente → Siguiente lección
 *   resto             → Continuar (a la primera sin ver)
 */
export function BarraSiguiente({
  curso,
  progreso,
  indice,
}: {
  curso: CursoAcademy;
  progreso: ProgresoCurso;
  indice: number;
}) {
  const navigate = useNavigate();
  /* `0` cuando el curso ya está aprobado: con los minutos de verdad, `useEsperaExamen`
     arranca un `setInterval` de UN SEGUNDO para dibujar un contador que en este caso no
     se muestra —aprobado no se repite—, y ese tick re-renderiza toda la barra (y con
     ella el modal del certificado, que cuelga de acá) una vez por segundo
     (ADR-2026-08-25-la-barra-no-tiquea-sobre-un-curso-aprobado). Con 0 no hay intervalo. */
  const espera = useEsperaExamen(
    progreso.ultimoIntento,
    progreso.aprobado || !EXAMEN_HABILITADO ? 0 : curso.esperaMinutos,
  );
  const siguiente = curso.lecciones[indice + 1];
  const { setMobileHideBottomNav } = useSidebar();

  /* "Modo tarea": la pantalla monta su propia barra de acción abajo, así que la
     navegación inferior de la app se aparta — el mismo mecanismo que usan la
     selección de productos y el paso Configurar de Interfaz V2
     (ADR-2026-08-04-el-modo-se-lleva-la-barra).

     Sin esto las dos comparten el borde inferior y el mismo `z-40`: la nav quedaba
     encima y el botón de seguir no se veía. Apilarlas era la otra opción, pero son
     140 px de cromo fijo sobre un teléfono, comiéndose el video. */
  useEffect(() => {
    setMobileHideBottomNav(true);
    return () => setMobileHideBottomNav(false);
  }, [setMobileHideBottomNav]);

  const irA = (slug: string) => navigate(`/academia/${curso.slug}/${slug}`);
  const clases =
    'flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-[15px] font-semibold transition-colors';

  const contenido = !EXAMEN_HABILITADO ? (
    /* Sin examen (ADR-2026-10-07-la-academia-sin-examen-por-ahora) la barra sólo
       recorre: la siguiente lección, la primera sin ver, y con el curso terminado
       la vuelta a la academia en vez de una acción que ya no existe. */
    siguiente ? (
      <button
        type="button"
        onClick={() => irA(siguiente.slug)}
        className={`${clases} bg-brand-primary text-white active:bg-brand-primary-hover`}
      >
        Siguiente lección
        <ArrowRight className="h-4.5 w-4.5" strokeWidth={2.2} />
      </button>
    ) : progreso.completo ? (
      <button
        type="button"
        onClick={() => navigate('/academia')}
        className={`${clases} bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-brand-primary active:bg-[rgb(var(--brand-primary-rgb)_/_0.135)]`}
      >
        <BadgeCheck className="h-4.5 w-4.5" strokeWidth={2.2} />
        Curso completado · Ver cursos
      </button>
    ) : (
      <button
        type="button"
        onClick={() => {
          const pendiente = curso.lecciones.find((l) => l.id === progreso.siguienteLeccionId);
          if (pendiente) irA(pendiente.slug);
        }}
        className={`${clases} bg-brand-primary text-white active:bg-brand-primary-hover`}
      >
        Continuar
        <ArrowRight className="h-4.5 w-4.5" strokeWidth={2.2} />
      </button>
    )
  ) : progreso.aprobado ? (
    <BotonCertificado curso={curso} variante="barra" />
  ) : progreso.completo ? (
    espera.listo ? (
      <button
        type="button"
        onClick={() => navigate(`/academia/${curso.slug}/examen`)}
        className={`${clases} bg-brand-primary text-white active:bg-brand-primary-hover`}
      >
        <BadgeCheck className="h-4.5 w-4.5" strokeWidth={2.2} />
        Tomar examen
      </button>
    ) : (
      <button type="button" disabled className={`${clases} bg-black/[0.06] text-black/40`}>
        <Clock className="h-4 w-4" strokeWidth={2.2} />
        Examen disponible en
        <span className="tabular-nums">{espera.etiqueta}</span>
      </button>
    )
  ) : siguiente ? (
    <button
      type="button"
      onClick={() => irA(siguiente.slug)}
      className={`${clases} bg-brand-primary text-white active:bg-brand-primary-hover`}
    >
      Siguiente lección
      <ArrowRight className="h-4.5 w-4.5" strokeWidth={2.2} />
    </button>
  ) : (
    /* Última lección de la lista pero quedan salteadas: seguir es volver a la
       primera sin ver, no quedarse sin acción. */
    <button
      type="button"
      onClick={() => {
        const pendiente = curso.lecciones.find((l) => l.id === progreso.siguienteLeccionId);
        if (pendiente) irA(pendiente.slug);
      }}
      className={`${clases} bg-brand-primary text-white active:bg-brand-primary-hover`}
    >
      Continuar
      <ArrowRight className="h-4.5 w-4.5" strokeWidth={2.2} />
    </button>
  );

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-black/[0.06] bg-white/95 px-4 pt-3 backdrop-blur-md lg:hidden"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
    >
      {contenido}
    </div>
  );
}
